
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UploadDocumentFormProps {
  sectionId: string;
  onDocumentUploaded: () => void;
  organizationId: string;
}

export const UploadDocumentForm = ({ sectionId, onDocumentUploaded, organizationId }: UploadDocumentFormProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const memberId = sessionStorage.getItem('organization_member_id');

      if (!organizationId || !memberId) {
        throw new Error('Missing organization or member ID');
      }

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `${organizationId}/${sectionId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the current max position for the section
      const { data: positionData } = await supabase
        .from('documents')
        .select('position')
        .eq('section_id', sectionId)
        .order('position', { ascending: false })
        .limit(1);

      const newPosition = (positionData?.[0]?.position || 0) + 1;

      // Create document record in database
      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          name: file.name,
          display_name: displayName.trim() || null,
          file_path: filePath,
          section_id: sectionId,
          organization_id: organizationId,
          created_by: memberId,
          size: file.size,
          content_type: file.type,
          position: newPosition
        });

      if (dbError) throw dbError;

      toast.success('Document uploaded successfully');
      onDocumentUploaded();
      setFile(null);
      setDisplayName('');
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleUpload} className="space-y-4">
      <Input
        type="file"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        disabled={isUploading}
      />
      <Input
        type="text"
        placeholder="Display name (optional)"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        disabled={isUploading}
      />
      <Button 
        type="submit" 
        disabled={!file || isUploading}
        className="w-full"
      >
        <Upload className="h-4 w-4 mr-2" />
        {isUploading ? 'Uploading...' : 'Upload Document'}
      </Button>
    </form>
  );
};
